import { useCustomer } from '@adapters/bigcommerce/hooks/customer';
import DealsBanner from '_components/UI/DealsBanner';
import { useHasMounted } from '@hooks/useHasMounted/useHasMounted';
import { useTranslation, Trans } from 'next-i18next';
import { useUI } from '@context/GlobalUIContext';
import SimpleLink from '_components/UI/SimpleLink';
import { trackBuyBoxEvent } from '@utils/gtm/ga4/trackBuyBoxEvents';

import styles from './MemberPricingDealsBanner.module.scss';

const LoginButton = ({ children }: { children?: string }) => {
  const { openLoginModal } = useUI();

  const handleClick = () => {
    trackBuyBoxEvent({
      eventName: 'click_mp_signin',
      linkText: children ?? '',
      linkUrl: null,
    });
    openLoginModal();
  };

  return (
    <button type="button" className={styles.inlineAction} onClick={handleClick}>
      {children}
    </button>
  );
};

const JoinLink = ({ children, href }: { children?: string; href: string }) => {
  const handleClick = () => {
    trackBuyBoxEvent({
      eventName: 'click_mp_join',
      linkText: children ?? '',
      linkUrl: href,
    });
  };

  return (
    <SimpleLink
      href={href}
      variant="secondary"
      size="small"
      onClick={handleClick}
    >
      {children}
    </SimpleLink>
  );
};

export default function MemberPricingDealsBanner({
  isPlp,
}: {
  isPlp?: boolean;
}) {
  const { t, i18n } = useTranslation('common');
  const hasMounted = useHasMounted();
  const { customerIsLoggedIn } = useCustomer();
  const locale = i18n.language ?? 'en';
  const registerHref = `/${locale}/auth/register`;
  const loggedOutDescription = (
    <Trans
      i18nKey="product.info.banner.membership.discount.description.loggedOut"
      ns="common"
      components={{
        login: <LoginButton />,
        join: <JoinLink href={registerHref} />,
      }}
    />
  );

  return (
    <div className={styles.infoBannerContainer}>
      <DealsBanner
        variant="membershipDiscount"
        title={
          hasMounted && customerIsLoggedIn
            ? t('common:product.info.banner.membership.discount.title.loggedIn')
            : t(
                'common:product.info.banner.membership.discount.title.loggedOut'
              )
        }
        descriptionLine={
          isPlp
            ? undefined
            : hasMounted && customerIsLoggedIn
            ? t(
                'common:product.info.banner.membership.discount.description.loggedIn'
              )
            : loggedOutDescription
        }
        infoTooltipContent={
          t('common:product.info.banner.membership.discount.tooltip') +
          (isPlp && hasMounted && !customerIsLoggedIn
            ? t(
                'common:product.info.banner.membership.discount.tooltip.plpSuffix'
              )
            : '')
        }
        tooltipPlacement={'title'}
      />
    </div>
  );
}
